import React, { useEffect, useState } from 'react';
import Button from '../common/Button';
import GradientButton from '../common/GradientButton';
import Input from '../common/Input';
import JalaliDatePicker from '../common/JalaliDatePicker';
import Modal from '../common/Modal';
import {
  createClubApi,
  updateClubApi,
} from '../../api/club.api';

const EMPTY_FORM = {
  name: '',
  establishmentDate: '',
  licenseNumber: '',
  address: '',
  managerFullName: '',
  managerEmail: '',
  managerPhone: '',
  phone: '',
  email: '',
  city: '',
  province: '',
  websiteUrl: '',
  instagramUrl: '',
  telegramUrl: '',
  description: '',
  managerPassword: '',
};

const normalizeClub = (club) => ({
  name: club?.name || '',
  establishmentDate: club?.establishmentDate || '',
  licenseNumber: club?.licenseNumber || '',
  address: club?.address || '',

  managerFullName:
    club?.manager?.fullName || '',

  managerEmail:
    club?.manager?.email || '',

  managerPhone:
    club?.manager?.phone || '',

  phone: club?.phone || '',
  email: club?.email || '',
  city: club?.city || '',
  province: club?.province || '',
  websiteUrl: club?.websiteUrl || '',
  instagramUrl: club?.instagramUrl || '',
  telegramUrl: club?.telegramUrl || '',
  description: club?.description || '',
});

export default function ClubForm({
  open,
  onClose,
  onSaved,
  club,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isEdit = Boolean(club);

  /* =========================================================
     INITIALIZE FORM
  ========================================================= */

  useEffect(() => {
    if (!open) return;

    setError('');
    setForm(
      club
        ? normalizeClub(club)
        : { ...EMPTY_FORM }
    );
  }, [club, open]);

  /* =========================================================
     CHANGE HANDLERS
  ========================================================= */

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (date) => {
    setForm((prev) => ({
      ...prev,
      establishmentDate: date || '',
    }));
  };

  /* =========================================================
     SUBMIT
  ========================================================= */

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submitting) return;

    setError('');

    const payload = {
      ...form,
      name: form.name.trim(),
      managerFullName: form.managerFullName.trim(),
      managerEmail: form.managerEmail.trim(),
      managerPassword: form.managerPassword?.trim() || undefined,
    };

    if (!payload.name) {
      setError('نام باشگاه را وارد کنید.');
      return;
    }

    if (!payload.establishmentDate) {
      setError('تاریخ تأسیس باشگاه را وارد کنید.');
      return;
    }

    if (!isEdit) {
      if (!payload.managerFullName) {
        setError('نام مدیر باشگاه را وارد کنید.');
        return;
      }

      if (!payload.managerEmail) {
        setError('ایمیل مدیر باشگاه را وارد کنید.');
        return;
      }
    }

    setSubmitting(true);

    try {
      if (isEdit) {
        await updateClubApi(
          club.id,
          payload
        );
      } else {
        await createClubApi(payload);
      }

      await onSaved?.();

      setForm({ ...EMPTY_FORM });
      onClose?.();
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'عملیات با خطا مواجه شد. لطفاً اطلاعات را بررسی کنید.';

      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  /* =========================================================
     CLOSE
  ========================================================= */

  const handleClose = () => {
    if (submitting) return;

    setError('');
    onClose?.();
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        isEdit
          ? 'ویرایش باشگاه'
          : 'ثبت باشگاه جدید'
      }
      size="lg"
    >
      <form
        onSubmit={handleSubmit}
        className="space-y-5"
      >
        {/* ERROR */}
        {error && (
          <div className="flex items-start gap-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
            <div className="mt-0.5 shrink-0">
              <span className="font-black">!</span>
            </div>

            <div className="flex-1 leading-6">
              {error}
            </div>

            <button
              type="button"
              onClick={() => setError('')}
              className="shrink-0 rounded-lg p-1 text-red-400 transition hover:bg-red-100 hover:text-red-600"
              aria-label="بستن پیام خطا"
            >
              ✕
            </button>
          </div>
        )}

        {/* =====================================================
            CLUB INFORMATION
        ====================================================== */}

        <section>
          <div className="mb-4 flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-50 text-emerald-600">
              <span className="text-sm font-black">
                ۱
              </span>
            </div>

            <div>
              <h3 className="text-sm font-black text-text-dark">
                اطلاعات باشگاه
              </h3>

              <p className="mt-0.5 text-[11px] text-slate-400">
                اطلاعات اصلی و مشخصات باشگاه
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Input
              label="نام باشگاه"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
            />

            <JalaliDatePicker
              label="تاریخ تأسیس"
              value={form.establishmentDate}
              onChange={handleDateChange}
              required
            />

            <Input
              label="شماره مجوز"
              name="licenseNumber"
              value={form.licenseNumber}
              onChange={handleChange}
            />

            <Input
              label="تلفن باشگاه"
              name="phone"
              value={form.phone}
              onChange={handleChange}
            />

            <Input
              label="ایمیل باشگاه"
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
            />

            <Input
              label="شهر"
              name="city"
              value={form.city}
              onChange={handleChange}
            />

            <Input
              label="استان"
              name="province"
              value={form.province}
              onChange={handleChange}
            />

            <Input
              label="آدرس"
              name="address"
              value={form.address}
              onChange={handleChange}
              className="md:col-span-2"
            />
          </div>
        </section>

        {/* =====================================================
            MANAGER
        ====================================================== */}

        {!isEdit && (
          <section className="border-t border-slate-200 pt-5">
            <div className="mb-4 flex items-center gap-2">
              <div className="grid h-8 w-8 place-items-center rounded-lg bg-blue-50 text-blue-600">
                <span className="text-sm font-black">
                  ۲
                </span>
              </div>

              <div>
                <h3 className="text-sm font-black text-text-dark">
                  اطلاعات مدیر باشگاه
                </h3>

                <p className="mt-0.5 text-[11px] text-slate-400">
                  اطلاعات حساب مدیر باشگاه
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Input
                label="نام و نام خانوادگی مدیر"
                name="managerFullName"
                value={form.managerFullName}
                onChange={handleChange}
                required
              />

              <Input
                label="ایمیل مدیر"
                type="email"
                name="managerEmail"
                value={form.managerEmail}
                onChange={handleChange}
                required
              />

              <Input
                label="تلفن مدیر"
                name="managerPhone"
                value={form.managerPhone}
                onChange={handleChange}
              />
              <Input
                label="رمز عبور مدیر (اختیاری)"
                type="text"
                name="managerPassword"
                value={form.managerPassword}
                onChange={handleChange}
                placeholder="خالی بگذارید تا رمز تصادفی ساخته شود"
              />
            </div>
          </section>
        )}

        {/* =====================================================
            SOCIAL / WEB
        ====================================================== */}

        <section className="border-t border-slate-200 pt-5">
          <div className="mb-4 flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-violet-50 text-violet-600">
              <span className="text-sm font-black">
                {isEdit ? '۲' : '۳'}
              </span>
            </div>

            <div>
              <h3 className="text-sm font-black text-text-dark">
                اطلاعات آنلاین
              </h3>

              <p className="mt-0.5 text-[11px] text-slate-400">
                وب‌ سایت و شبکه‌های اجتماعی باشگاه
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Input
              label="وب‌سایت"
              name="websiteUrl"
              value={form.websiteUrl}
              onChange={handleChange}
              placeholder="https://example.com"
            />

            <Input
              label="اینستاگرام"
              name="instagramUrl"
              value={form.instagramUrl}
              onChange={handleChange}
              placeholder="https://instagram.com/..."
            />

            <Input
              label="تلگرام"
              name="telegramUrl"
              value={form.telegramUrl}
              onChange={handleChange}
              placeholder="https://t.me/..."
            />

            <Input
              label="توضیحات"
              name="description"
              value={form.description}
              onChange={handleChange}
              className="md:col-span-2"
            />
          </div>
        </section>

        {/* =====================================================
            ACTIONS
        ====================================================== */}

        <div className="flex flex-col-reverse gap-2 border-t border-slate-200 pt-5 sm:flex-row sm:justify-end">
          <Button
            variant="ghost"
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="h-11 rounded-xl px-5"
          >
            انصراف
          </Button>

          <GradientButton
            type="submit"
            disabled={submitting}
            className="h-11 rounded-xl px-6"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                در حال ذخیره...
              </span>
            ) : isEdit ? (
              'ذخیره تغییرات'
            ) : (
              'ثبت باشگاه'
            )}
          </GradientButton>
        </div>
      </form>
    </Modal>
  );
}